import { DateTime } from "luxon";

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** True for a date with no time of day (stored as midnight UTC), shown as that calendar day. */
const isDateOnly = (parsed: Date): boolean => {
  return parsed.getUTCHours() === 0
    && parsed.getUTCMinutes() === 0
    && parsed.getUTCSeconds() === 0
    && parsed.getUTCMilliseconds() === 0;
};

const formatDateOnly = (parsed: Date): string => {
  return parsed.toLocaleDateString(undefined, { timeZone: "UTC" });
};

/** An ISO date / time in the browser's locale; a date-only value as its day. "—" when empty; unparseable values as-is. */
export const formatDateTime = (iso: string | undefined): string => {
  if (!iso) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  if (isDateOnly(parsed)) {
    return formatDateOnly(parsed);
  }
  return parsed.toLocaleString();
};

const formatParsedDayAgo = (parsed: Date, now: number): string => {
  const base = DateTime.fromMillis(now);
  let day = DateTime.fromJSDate(parsed).startOf("day");
  let date = parsed.toLocaleDateString();
  if (isDateOnly(parsed)) {
    day = DateTime.fromObject({
      day: parsed.getUTCDate(),
      month: parsed.getUTCMonth() + 1,
      year: parsed.getUTCFullYear(),
    });
    date = formatDateOnly(parsed);
  }
  const daysAgo = base.startOf("day").diff(day, "days").days;
  if (daysAgo === 0 || daysAgo === 1) {
    const relativeDay = day.toRelativeCalendar({ base, unit: "days" }) ?? "";
    return relativeDay.charAt(0).toUpperCase() + relativeDay.slice(1);
  }
  return date;
};

/** The day only: "Today", "Yesterday", then the date. `now` decides when the label refreshes. */
export const formatDayAgo = (iso: string | undefined, now: number): string => {
  if (!iso) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return formatParsedDayAgo(parsed, now);
};

/**
 * How long ago while recent ("just now", "5 minutes ago"), then the date and time once a day old.
 * A date-only value reads as formatDayAgo. `now` decides when the label refreshes.
 */
export const formatTimeAgo = (iso: string | undefined, now: number): string => {
  if (!iso) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  if (isDateOnly(parsed)) {
    return formatParsedDayAgo(parsed, now);
  }

  const base = DateTime.fromMillis(now);
  const ageMs = now - parsed.getTime();
  if (ageMs >= -MINUTE_MS && ageMs < MINUTE_MS) {
    return "just now";
  }
  if (ageMs < -MINUTE_MS || ageMs >= DAY_MS) {
    return parsed.toLocaleString();
  }
  const time = DateTime.fromJSDate(parsed);
  return time.toRelative({ base, unit: ["hours", "minutes"] }) ?? parsed.toLocaleString();
};

/** A moment as "21 Oct, 22:33" (no year), in the browser's zone; "—" when there's none. */
export const formatDayTime = (iso: string | undefined): string => {
  if (!iso) return "—";
  const parsed = DateTime.fromISO(iso);
  if (!parsed.isValid) return iso;
  return parsed.toFormat("d LLL, HH:mm");
};
